import React, { useEffect } from 'react'; // Added useEffect
import styled from 'styled-components';
import { Carousel, Button, Typography, Space } from 'antd';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom'; // Added useLocation
import { PageFade } from '../components/PageTransition';
import { rooms, bookingLink } from '../data/hotelData';
import { Helmet } from 'react-helmet';
import RoomGallery from '../components/RoomGallery';
import { CheckCircleOutlined } from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;

// --- Styled Components ---

const HeroSection = styled.div`
  height: 60vh;
  /* You can change this image to a generic "Rooms" banner if you prefer */
  background: url('https://firebasestorage.googleapis.com/v0/b/miran-suites.firebasestorage.app/o/Rooms%20Page%2Fpremium-room-with-living-area-04.jpg?alt=media&token=af641638-4fc8-4bdd-86f9-68c88d7a337b') center/cover no-repeat fixed;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  text-align: center;
  color: white;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(0,0,0,0.5);
  }
  
  > div {
    position: relative;
    z-index: 2;
  }
`;

const PageContainer = styled.div`
  background: #fcfcfc;
  padding-top: 6rem; 
  padding-bottom: 6rem;
`;

const RoomRow = styled(motion.div)`
  display: flex;
  height: 600px; 
  background: #fff;
  margin-bottom: 5rem;
  box-shadow: 0 15px 50px rgba(0,0,0,0.08);
  
  /* Scroll Margin ensures the title isn't hidden behind the navbar when scrolling */
  scroll-margin-top: 100px; 

  flex-direction: ${props => props.$reversed ? 'row-reverse' : 'row'};

  @media (max-width: 992px) {
    flex-direction: column !important; 
    height: auto; 
    margin-bottom: 3rem;
    border-radius: 0;
    box-shadow: none;
    border-bottom: 1px solid #eee;
  }
`;

const VisualSide = styled.div`
  flex: 1.3; 
  position: relative;
  background: #ffffff;
  overflow: hidden;

  .ant-carousel, .slick-slider, .slick-list, .slick-track, .slick-slide, .slick-slide > div {
    height: 100% !important;
  }

  .slick-dots {
    bottom: 20px;
    z-index: 10;
    li {
      margin: 0 4px;
      button {
        height: 4px;
        background: #fff !important;
        opacity: 0.6;
      }
      &.slick-active button {
        opacity: 1;
        width: 25px;
        background: #d4af37 !important;
      }
    }
  }

  img {
    width: 100%;
    height: 100%;
    min-height: 300px;
    object-fit: cover; 
    display: block;
  }

  @media (max-width: 992px) {
    height: 300px; 
    flex: none; 
  }
`;

const InfoSide = styled.div`
  flex: 0.7; 
  padding: 4rem;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: 992px) {
    padding: 2rem 1.5rem;
    align-items: center;
    text-align: center;
  }
`;

const RoomTitle = styled(Title)`
  && {
    font-family: 'Playfair Display', serif;
    font-size: 2.8rem;
    margin-bottom: 0.5rem;
    color: #1a1a1a;
    line-height: 1.2;
    
    @media (max-width: 768px) { font-size: 2rem; }
  }
`;

const RoomMeta = styled.div`
  font-family: 'Lato', sans-serif;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: #999;
  margin-bottom: 1.5rem;
  font-size: 0.75rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 1rem;

  span { color: #d4af37; } 
`;

const Description = styled(Paragraph)`
  && {
    font-size: 1.05rem;
    line-height: 1.8;
    color: #555;
    margin-bottom: 2rem;
    max-width: 450px;
  }
`;

const AmenitiesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.8rem;
  margin-bottom: 2.5rem;
  width: 100%;
  max-width: 450px;

  @media (max-width: 768px) {
    text-align: left;
    margin-bottom: 2rem;
    grid-template-columns: 1fr; 
    gap: 0.5rem;
  }
`;

const AmenityItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #444;
  font-size: 0.9rem;

  .anticon { color: #d4af37; }
`;

const PriceTag = styled.div`
  font-family: 'Playfair Display', serif;
  font-size: 2rem;
  color: #d4af37;
  line-height: 1;
  
  small {
    font-size: 0.85rem;
    color: #999;
    font-family: 'Lato', sans-serif;
    margin-left: 5px;
    letter-spacing: 0;
  }
`;

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-top: auto;
  padding-top: 2rem;
  border-top: 1px solid #f0f0f0;
  width: 100%;
  max-width: 450px;

  @media (max-width: 992px) {
    justify-content: center;
    flex-direction: column;
    gap: 1.2rem;
  }
`;

// --- Component ---

const Rooms = () => {
  const location = useLocation();

  // Handle Scroll to Room
  useEffect(() => {
    if (location.hash) {
      // Small timeout allows page to render before scrolling
      setTimeout(() => {
        const id = location.hash.replace('#', '');
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location]);

  return (
    <PageFade>
      <Helmet>
        <title>Our Rooms & Suites | Miran Suites</title>
        <meta name="description" content="Browse our Deluxe, Premium, Studio, and Presidential Suites. Check availability and book your stay." />
      </Helmet>

      {/* Hero Header */}
      <HeroSection>
        <div>
          <h1 style={{ fontFamily: 'Playfair Display', fontSize: '4rem', margin: 0 }}>The Collection</h1>
          <p style={{ fontSize: '1.2rem', letterSpacing: '3px', opacity: 0.9, marginTop: '1rem', textTransform: 'uppercase' }}>
            Curated comfort for the discerning
          </p>
        </div>
      </HeroSection>

      <PageContainer>
        {rooms.map((room, index) => (
          <RoomRow 
            key={room.id}
            id={room.id} // <--- ID ADDED HERE for navigation
            $reversed={index % 2 !== 0}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
          >
            {/* VISUAL SIDE */}
            <VisualSide>
              <Carousel autoplay effect="fade" speed={1000} autoplaySpeed={5000} dots={true}>
                {room.images.map((img, i) => (
                  <div key={i}>
                    <img src={img} alt={`${room.title} view ${i+1}`} />
                  </div>
                ))}
              </Carousel>
            </VisualSide>

            {/* INFO SIDE */}
            <InfoSide>
              <RoomMeta>
                <span>{room.size}</span>
                <span style={{ color: '#ddd' }}>|</span> 
                Occupancy: 2 Adults
              </RoomMeta>
              
              <RoomTitle level={2}>{room.title}</RoomTitle>
              
              <Description>{room.description}</Description>

              <AmenitiesGrid>
                {room.amenities.map(item => (
                  <AmenityItem key={item}>
                    <CheckCircleOutlined /> {item}
                  </AmenityItem>
                ))}
              </AmenitiesGrid>

              <ActionRow>
                <div style={{ textAlign: 'left' }}>
                  <Text type="secondary" style={{ display: 'block', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px' }}>Starting from</Text>
                  <PriceTag>
                    {room.price}<small>/ night</small>
                  </PriceTag>
                </div>

                <Space size="middle">
                  <RoomGallery images={room.images} title={room.title} />
                  
                  <Button 
                    type="primary" 
                    size="large"
                    href={bookingLink}
                    target="_blank"
                    style={{ 
                      height: '45px', 
                      padding: '0 30px', 
                      background: '#1a1a1a', 
                      borderColor: '#1a1a1a',
                      fontSize: '0.85rem',
                      letterSpacing: '1px',
                      textTransform: 'uppercase',
                      borderRadius: '0'
                    }}
                  >
                    Book Now
                  </Button>
                </Space>
              </ActionRow>
            </InfoSide>
          </RoomRow>
        ))}
      </PageContainer>
    </PageFade>
  );
};

export default Rooms;